import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export type Theme = 'light' | 'dark' | 'system';

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private readonly THEME_KEY = 'app-theme';
  private readonly DEFAULT_THEME: Theme = 'light';

  private themeSubject = new BehaviorSubject<Theme>(this.DEFAULT_THEME);
  public theme$ = this.themeSubject.asObservable();

  private systemThemeSubject = new BehaviorSubject<'light' | 'dark'>('light');
  public systemTheme$ = this.systemThemeSubject.asObservable();

  private activeThemeSubject = new BehaviorSubject<'light' | 'dark'>('light');
  public activeTheme$ = this.activeThemeSubject.asObservable();

  constructor() {
    this.initializeTheme();
    this.setupSystemThemeListener();
  }

  private initializeTheme(): void {
    // Get saved theme from localStorage or use default
    const savedTheme = this.getSavedTheme();
    this.themeSubject.next(savedTheme);
    this.applyTheme(savedTheme);
  }

  private getSavedTheme(): Theme {
    if (typeof window === 'undefined') return this.DEFAULT_THEME;

    const saved = localStorage.getItem(this.THEME_KEY) as Theme;
    return saved && ['light', 'dark', 'system'].includes(saved) ? saved : this.DEFAULT_THEME;
  }

  private saveTheme(theme: Theme): void {
    if (typeof window === 'undefined') return;
    localStorage.setItem(this.THEME_KEY, theme);
  }

  private setupSystemThemeListener(): void {
    if (typeof window === 'undefined') return;

    // Initial system theme detection
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    this.systemThemeSubject.next(mediaQuery.matches ? 'dark' : 'light');

    // Listen for system theme changes
    mediaQuery.addEventListener('change', (e) => {
      this.systemThemeSubject.next(e.matches ? 'dark' : 'light');

      // If current theme is 'system', reapply theme
      if (this.themeSubject.value === 'system') {
        this.applySystemTheme();
      }
    });
  }

  private applyTheme(theme: Theme): void {
    if (typeof window === 'undefined') return;

    const htmlElement = document.documentElement;

    // Remove existing theme classes
    htmlElement.classList.remove('light', 'dark');

    switch (theme) {
      case 'light':
        htmlElement.classList.add('light');
        this.activeThemeSubject.next('light');
        break;
      case 'dark':
        htmlElement.classList.add('dark');
        this.activeThemeSubject.next('dark');
        break;
      case 'system':
        this.applySystemTheme();
        break;
    }
  }

  private applySystemTheme(): void {
    if (typeof window === 'undefined') return;

    const htmlElement = document.documentElement;
    const isDark = window.matchMedia('(prefers-color-scheme: dark)').matches;

    htmlElement.classList.remove('light', 'dark');
    htmlElement.classList.add(isDark ? 'dark' : 'light');
    this.activeThemeSubject.next(isDark ? 'dark' : 'light');
  }

  public setTheme(theme: Theme): void {
    this.themeSubject.next(theme);
    this.saveTheme(theme);
    this.applyTheme(theme);
  }

  public getCurrentTheme(): Theme {
    return this.themeSubject.value;
  }

  public getActiveTheme(): 'light' | 'dark' {
    return this.activeThemeSubject.value;
  }

  public toggleTheme(): void {
    const current = this.getCurrentTheme();
    const next: Theme = current === 'light' ? 'dark' : current === 'dark' ? 'system' : 'light';
    this.setTheme(next);
  }

  public isDarkMode(): boolean {
    return this.getActiveTheme() === 'dark';
  }

  public isLightMode(): boolean {
    return this.getActiveTheme() === 'light';
  }

  public isSystemMode(): boolean {
    return this.getCurrentTheme() === 'system';
  }
}
